import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Users, Image as ImageIcon, Calendar, ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Photo {
  id: string;
  image_url: string;
  caption: string | null;
  year: number;
  display_order: number;
}

const KidsCamp = () => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const { data, error } = await supabase
        .from('kids_camp_photos')
        .select('*')
        .order('year', { ascending: false })
        .order('display_order', { ascending: true });

      if (error) throw error;
      setPhotos(data || []);

      if (data && data.length > 0) {
        const mostRecentYear = Math.max(...data.map(p => p.year));
        setSelectedYear(mostRecentYear);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const availableYears = Array.from(new Set(photos.map(p => p.year))).sort((a, b) => b - a);
  const selectedYearPhotos = selectedYear === 'all'
    ? photos
    : photos.filter(p => p.year === selectedYear);

  return (
    <div className="min-h-screen flex flex-col bg-[#111111]">
      <Navbar />
      
      <main className="flex-1 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <Users className="w-8 h-8 text-cardinal" />
            <h1 className="text-4xl font-bold text-white">Cardinals Kids Camp</h1>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cardinal mx-auto"></div>
              <p className="text-gray-400 mt-4">Loading photos...</p>
            </div>
          ) : (
            <>
              {availableYears.length > 0 && (
                <div className="mb-8">
                  <div className="relative inline-block">
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                      className="appearance-none bg-[#222222] text-white px-6 py-3 pr-10 rounded-lg border border-white/10 focus:border-cardinal focus:outline-none cursor-pointer"
                    >
                      {availableYears.map((year) => {
                        const yearPhotoCount = photos.filter(p => p.year === year).length;
                        return (
                          <option key={year} value={year}>
                            {year} Camp Photos ({yearPhotoCount} photos)
                          </option>
                        );
                      })}
                      <option value="all">All Years ({photos.length} photos)</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              )}

              <div className="bg-[#222222] rounded-lg p-8 mb-16">
                <div className="flex items-center gap-3 mb-8">
                  <ImageIcon className="w-6 h-6 text-cardinal" />
                  <h2 className="text-2xl font-bold text-white">
                    {selectedYear === 'all' ? 'All Camp Photos' : `${selectedYear} Camp Photos`}
                  </h2>
                  <span className="text-gray-400">({selectedYearPhotos.length} photos)</span>
                </div>

                {selectedYearPhotos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {selectedYearPhotos.map((photo) => (
                      <div
                        key={photo.id}
                        className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity bg-[#1A1A1A]"
                        onClick={() => setSelectedImage(photo.image_url)}
                      >
                        <img
                          src={photo.image_url}
                          alt={photo.caption || `Camp photo ${photo.year}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">No photos available for {selectedYear}</p>
                    <p className="text-gray-500">Check back soon for camp photos!</p>
                  </div>
                )}

                <div className="mt-12 text-center">
                  <p className="text-gray-400 text-lg">
                    Keep an eye out for future Cardinals Kids Camps! Follow us on social media or join our mailing list to stay updated on upcoming camp dates and registration information.
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Image Modal */}
          {selectedImage && (
            <div 
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
            >
              <div className="max-w-6xl w-full">
                <img 
                  src={selectedImage}
                  alt="Selected camp photo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default KidsCamp;